import { PAGE_REGISTRY } from './pageRegistry';

/** Parallel chunk downloads — balances speed vs. overwhelming the network. */
const PRELOAD_CONCURRENCY = 8;

/** Shell routes needed for the authenticated workspace (launcher + in-app navigation). */
const CORE_ROUTE_LOADERS = [
  {
    id: 'core-apps-launcher',
    label: 'App launcher',
    phase: 'core',
    loader: () => import('../pages/shared/AppsPage'),
  },
  {
    id: 'core-app-layout',
    label: 'Workspace layout',
    phase: 'core',
    loader: () => import('../layouts/AppLayout'),
  },
  {
    id: 'core-not-found',
    label: 'Navigation',
    phase: 'core',
    loader: () => import('../pages/shared/NotFoundPage'),
  },
  {
    id: 'core-legacy-dashboard',
    label: 'Dashboard links',
    phase: 'core',
    loader: () => import('../pages/shared/LegacyDashboardRedirectPage'),
  },
  {
    id: 'core-unauthorized',
    label: 'Access controls',
    phase: 'core',
    loader: () => import('../pages/shared/UnauthorizedPage'),
  },
];

function collectPreloadEntries() {
  const seen = new Set();
  const entries = [];

  const add = (entry) => {
    if (!entry?.loader || seen.has(entry.id)) return;
    seen.add(entry.id);
    entries.push(entry);
  };

  CORE_ROUTE_LOADERS.forEach(add);
  PAGE_REGISTRY.forEach(add);

  return entries;
}

async function runLoader(entry) {
  try {
    await entry.loader();
    return { ok: true };
  } catch (error) {
    console.warn(`[bootstrap] Failed to preload "${entry.id}"`, error);
    return { ok: false, error };
  }
}

async function preloadSequential(entries, onProgress, state) {
  for (const entry of entries) {
    const result = await runLoader(entry);
    state.done += 1;
    if (!result.ok) state.failed += 1;
    onProgress?.({
      done: state.done,
      total: state.total,
      failed: state.failed,
      phase: entry.phase,
      label: entry.label,
    });
  }
}

async function preloadConcurrent(entries, onProgress, state) {
  const queue = [...entries];

  async function worker() {
    while (queue.length > 0) {
      const entry = queue.shift();
      if (!entry) return;

      const result = await runLoader(entry);
      state.done += 1;
      if (!result.ok) state.failed += 1;
      onProgress?.({
        done: state.done,
        total: state.total,
        failed: state.failed,
        phase: entry.phase,
        label: entry.label,
      });
    }
  }

  const workerCount = Math.min(PRELOAD_CONCURRENCY, Math.max(queue.length, 1));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

/**
 * Registers all route modules (via appsConfig import), preloads core shell first,
 * then preloads application modules in parallel batches.
 *
 * @param {(progress: { done: number, total: number, failed: number, phase?: string, label?: string }) => void} [onProgress]
 */
export async function preloadApplicationModules(onProgress) {
  await import('../appsConfig');

  const entries = collectPreloadEntries();
  const coreEntries = entries.filter((entry) => entry.phase === 'core');
  const moduleEntries = entries.filter((entry) => entry.phase !== 'core');

  const state = { done: 0, failed: 0, total: entries.length };

  onProgress?.({
    done: 0,
    total: state.total,
    failed: 0,
    phase: 'core',
    label: 'Core services',
  });

  await preloadSequential(coreEntries, onProgress, state);
  await preloadConcurrent(moduleEntries, onProgress, state);

  return {
    total: state.total,
    failed: state.failed,
    succeeded: state.total - state.failed,
  };
}
